import React, { useEffect } from 'react';
import { XMarkIcon, CreditCardIcon, CalendarIcon, ExclamationCircleIcon, ArrowPathIcon, CheckCircleIcon, StopCircleIcon, CircleStackIcon } from '@heroicons/react/24/outline';
import { useParcelasNfe, useErpPagamentoNfe } from '../../hooks/useXmlTerceiros';
import { money, formatDate } from '../../utils/format';
import { btnSecundario } from '../ui/iris';

interface ParcelasDialogProps {
  nfeId: number | null;
  numeroNfe?: number;
  isOpen: boolean;
  onClose: () => void;
}

const ParcelasDialog: React.FC<ParcelasDialogProps> = ({ nfeId, numeroNfe, isOpen, onClose }) => {
  const { data: parcelasData, isLoading, error } = useParcelasNfe(nfeId ?? undefined, isOpen);
  const { data: erpData, isLoading: erpLoading, error: erpError } = useErpPagamentoNfe(nfeId ?? undefined, isOpen);

  // Fechar com ESC + travar scroll do body enquanto aberto.
  useEffect(() => {
    if (!isOpen) return;
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onEsc);
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onEsc);
      document.body.style.overflow = original;
    };
  }, [isOpen, onClose]);

  if (!isOpen || !nfeId) return null;

  // Mescla XML × ERP por posição (após ordenação por vencimento já feita no backend).
  const xmlParcelas = parcelasData?.parcelas ?? [];
  const erpParcelas = erpData?.parcelas ?? [];
  const maxLen = Math.max(xmlParcelas.length, erpParcelas.length);
  const mergedRows = Array.from({ length: maxLen }, (_, i) => ({ xml: xmlParcelas[i], erp: erpParcelas[i] }));

  const totalXml = xmlParcelas.reduce((sum, p) => sum + (p.valor || 0), 0);
  const totalErp = erpParcelas.reduce((sum, p) => sum + (p.valorParcela || 0), 0);
  const totalPendente = erpParcelas.reduce((sum, p) => sum + (p.valorPendente || 0), 0);

  const sameDate = (a?: string, b?: string) => (!a || !b ? false : a.slice(0, 10) === b.slice(0, 10));
  const sameValue = (a?: number, b?: number) => (a == null || b == null ? false : Math.abs(a - b) < 0.01);

  const liquidadaTotal = !!erpData && erpData.faturada && erpData.parcelasLiquidadas === erpData.totalParcelas;

  return (
    <div className="fixed inset-0 z-50">
      <div className="fixed inset-0 bg-[var(--scrim)] backdrop-blur-[2px]" onClick={onClose} />

      {/* Mobile: full-screen. sm+: card centralizado. */}
      <div className="fixed inset-0 flex flex-col sm:items-center sm:justify-center sm:p-4">
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Parcelamento da NF-e"
          className="relative flex h-full w-full flex-col overflow-hidden bg-surface sm:h-auto sm:max-h-[90vh] sm:max-w-3xl sm:rounded-iris-lg sm:border sm:border-line-strong sm:shadow-iris-xl"
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
            <div className="flex min-w-0 items-center gap-2.5">
              <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-iris bg-accent">
                <CreditCardIcon className="h-4 w-4 text-white" />
              </span>
              <div className="min-w-0">
                <h2 className="truncate text-base font-semibold text-tx">Parcelamento{numeroNfe ? ` · NF-e ${numeroNfe}` : ''}</h2>
                <p className="truncate text-[11px] text-tx-3">Duplicatas e forma de pagamento</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-iris text-tx-3 transition-colors hover:bg-surface-2 hover:text-tx"
              aria-label="Fechar"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Conteúdo */}
          <div className="flex-1 space-y-4 overflow-y-auto p-4 sm:p-5">
            {isLoading && (
              <div className="flex items-center justify-center gap-2 py-10 text-sm text-tx-3">
                <ArrowPathIcon className="h-5 w-5 animate-spin" /> Carregando parcelamento…
              </div>
            )}

            {error && (
              <div className="flex items-start gap-2 rounded-iris border border-danger-border bg-danger-soft p-3 text-sm text-danger">
                <ExclamationCircleIcon className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>Não foi possível extrair as parcelas do XML desta NF-e.</span>
              </div>
            )}

            {!isLoading && !error && parcelasData && (
              <>
                {/* Fatura */}
                {parcelasData.fatura && (
                  <Grupo titulo="Fatura">
                    {parcelasData.fatura.numero && <Linha label="Número" value={parcelasData.fatura.numero} />}
                    {parcelasData.fatura.valorOriginal != null && <Linha label="Valor original" value={money(parcelasData.fatura.valorOriginal)} num />}
                    {parcelasData.fatura.valorDesconto != null && parcelasData.fatura.valorDesconto > 0 && (
                      <Linha label="Desconto" value={`- ${money(parcelasData.fatura.valorDesconto)}`} num valueClass="text-danger" />
                    )}
                    {parcelasData.fatura.valorLiquido != null && <Linha label="Valor líquido" value={money(parcelasData.fatura.valorLiquido)} num valueClass="text-ok" />}
                  </Grupo>
                )}

                {/* Formas de pagamento */}
                {parcelasData.pagamentos.length > 0 && (
                  <div>
                    <p className="mb-1.5 px-1 text-[10px] font-semibold uppercase tracking-wide text-tx-3">Forma de pagamento</p>
                    <div className="flex flex-wrap gap-2">
                      {parcelasData.pagamentos.map((pag, idx) => (
                        <div key={idx} className="inline-flex items-center gap-2 rounded-iris border border-line bg-surface-2 px-3 py-1.5 text-[13px]">
                          <CreditCardIcon className="h-3.5 w-3.5 text-tx-3" />
                          <span className="text-tx-2">{pag.tipoDescricao}</span>
                          {pag.valor > 0 && <span className="font-semibold tabular-nums text-tx">{money(pag.valor)}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Banner de situação no ERP */}
            {erpLoading && (
              <div className="flex items-center justify-center gap-2 rounded-iris bg-surface-2 py-3 text-sm text-tx-3">
                <ArrowPathIcon className="h-4 w-4 animate-spin" /> Consultando ERP…
              </div>
            )}

            {erpError && (
              <div className="flex items-start gap-2 rounded-iris border border-danger-border bg-danger-soft p-3 text-sm text-danger">
                <ExclamationCircleIcon className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>Não foi possível consultar o ERP.</span>
              </div>
            )}

            {!erpLoading && !erpError && erpData && (
              <div
                className={`rounded-iris border p-3 ${
                  !erpData.faturada ? 'border-warning-border bg-warning-soft'
                    : liquidadaTotal ? 'border-ok-border bg-ok-soft'
                    : 'border-line bg-accent-soft'
                }`}
              >
                <div className="flex items-start gap-3">
                  {!erpData.faturada ? (
                    <ExclamationCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-warning" />
                  ) : liquidadaTotal ? (
                    <CheckCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-ok" />
                  ) : (
                    <CircleStackIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-accent-text" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm font-semibold ${!erpData.faturada ? 'text-warning' : liquidadaTotal ? 'text-ok' : 'text-accent-text'}`}>
                      {!erpData.faturada ? 'Não faturada no ERP' : liquidadaTotal ? 'Totalmente liquidada no ERP' : 'Faturada · liquidação parcial'}
                    </p>
                    {erpData.faturada && (
                      <p className="mt-0.5 text-[13px] text-tx-2">
                        {erpData.parcelasLiquidadas}/{erpData.totalParcelas} parcela(s) liquidada(s) · Total {money(erpData.valorTotal)}
                        {erpData.valorPendente > 0 && (
                          <> · Pendente <span className="font-semibold text-danger">{money(erpData.valorPendente)}</span></>
                        )}
                      </p>
                    )}
                    {!erpData.faturada && erpData.mensagem && <p className="mt-0.5 text-[13px] text-tx-2">{erpData.mensagem}</p>}
                  </div>
                </div>
              </div>
            )}

            {/* Matriz XML × ERP */}
            {mergedRows.length > 0 && (
              <div>
                <div className="mb-1.5 flex items-center justify-between px-1">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-tx-3">Parcelas · XML × ERP</p>
                  <span className="rounded-full bg-surface-2 px-2 py-0.5 text-[11px] font-medium text-tx-2">
                    {mergedRows.length} {mergedRows.length === 1 ? 'parcela' : 'parcelas'}
                  </span>
                </div>

                {/* ── Mobile (< md): cards ── */}
                <div className="space-y-2 md:hidden">
                  {mergedRows.map((row, idx) => {
                    const { xml, erp } = row;
                    const vencMatch = xml && erp && sameDate(xml.dataVencimento, erp.vencimento);
                    const valMatch = xml && erp && sameValue(xml.valor, erp.valorParcela);
                    return (
                      <div key={idx} className={`rounded-iris border border-line p-3 ${erp?.liquidado ? 'bg-ok-soft' : 'bg-surface-2'}`}>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold text-tx">#{erp?.numeroParcela ?? xml?.numero ?? idx + 1}</span>
                          <StatusParcela erp={erp} />
                        </div>
                        <div className="mt-2 grid grid-cols-2 gap-2 text-[13px]">
                          <div>
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-accent-text">XML</p>
                            <p className="tabular-nums text-tx-2">{xml?.dataVencimento ? formatDate(xml.dataVencimento) : '—'}</p>
                            <p className="font-semibold tabular-nums text-tx">{xml ? money(xml.valor) : '—'}</p>
                          </div>
                          <div>
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-ok">ERP</p>
                            <p className="flex items-center gap-1 tabular-nums text-tx-2">
                              {erp?.vencimento ? formatDate(erp.vencimento) : '—'}
                              {erp && xml && !vencMatch && <span title="Vencimento divergente do XML" className="inline-block h-1.5 w-1.5 rounded-full bg-warning" />}
                            </p>
                            <p className="flex items-center gap-1 font-semibold tabular-nums text-tx">
                              {erp ? money(erp.valorParcela) : '—'}
                              {erp && xml && !valMatch && <span title="Valor divergente do XML" className="inline-block h-1.5 w-1.5 rounded-full bg-warning" />}
                            </p>
                          </div>
                        </div>
                        {erp && erp.valorPendente != null && erp.valorPendente > 0 && (
                          <p className="mt-2 border-t border-line pt-2 text-[13px] text-tx-3">
                            Pendente <span className="font-semibold text-danger">{money(erp.valorPendente)}</span>
                          </p>
                        )}
                      </div>
                    );
                  })}
                  {/* Totais (mobile) */}
                  <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 rounded-iris border border-line bg-surface px-3 py-2 text-[13px]">
                    <span><span className="text-[10px] uppercase tracking-wide text-tx-3">XML </span><span className="font-bold tabular-nums text-accent-text">{money(totalXml)}</span></span>
                    <span><span className="text-[10px] uppercase tracking-wide text-tx-3">ERP </span><span className="font-bold tabular-nums text-ok">{money(totalErp)}</span></span>
                    {totalPendente > 0 && <span className="ml-auto"><span className="text-[10px] uppercase tracking-wide text-tx-3">Pendente </span><span className="font-bold tabular-nums text-danger">{money(totalPendente)}</span></span>}
                  </div>
                </div>

                {/* ── Desktop (md+): tabela densa ── */}
                <div className="hidden overflow-x-auto rounded-iris border border-line md:block">
                  <table className="min-w-full divide-y divide-line text-[13px]">
                    <thead className="bg-surface-2">
                      <tr>
                        <th rowSpan={2} className="px-3 py-2 text-left align-bottom text-[10px] font-semibold uppercase tracking-wide text-tx-3">#</th>
                        <th colSpan={2} className="border-l border-line px-3 py-2 text-center text-[10px] font-semibold uppercase tracking-wide text-accent-text">XML (Duplicata)</th>
                        <th colSpan={3} className="border-l border-line px-3 py-2 text-center text-[10px] font-semibold uppercase tracking-wide text-ok">ERP (Contas a Pagar)</th>
                        <th rowSpan={2} className="border-l border-line px-3 py-2 text-left align-bottom text-[10px] font-semibold uppercase tracking-wide text-tx-3">Status</th>
                      </tr>
                      <tr>
                        <th className="border-l border-line px-3 py-1.5 text-left text-[10px] font-medium uppercase tracking-wide text-tx-3"><CalendarIcon className="mr-1 inline h-3 w-3" />Vencimento</th>
                        <th className="px-3 py-1.5 text-right text-[10px] font-medium uppercase tracking-wide text-tx-3">Valor</th>
                        <th className="border-l border-line px-3 py-1.5 text-left text-[10px] font-medium uppercase tracking-wide text-tx-3">Vencimento</th>
                        <th className="px-3 py-1.5 text-right text-[10px] font-medium uppercase tracking-wide text-tx-3">Valor</th>
                        <th className="px-3 py-1.5 text-right text-[10px] font-medium uppercase tracking-wide text-tx-3">Pendente</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-line">
                      {mergedRows.map((row, idx) => {
                        const { xml, erp } = row;
                        const vencMatch = xml && erp && sameDate(xml.dataVencimento, erp.vencimento);
                        const valMatch = xml && erp && sameValue(xml.valor, erp.valorParcela);
                        return (
                          <tr key={idx} className={erp?.liquidado ? 'bg-ok-soft' : 'transition-colors hover:bg-surface-2'}>
                            <td className="px-3 py-2 font-medium text-tx">{erp?.numeroParcela ?? xml?.numero ?? idx + 1}</td>
                            {/* XML */}
                            <td className="border-l border-line px-3 py-2 tabular-nums text-tx-2">{xml?.dataVencimento ? formatDate(xml.dataVencimento) : '—'}</td>
                            <td className="px-3 py-2 text-right font-semibold tabular-nums text-tx">{xml ? money(xml.valor) : '—'}</td>
                            {/* ERP */}
                            <td className="border-l border-line px-3 py-2 tabular-nums text-tx-2">
                              <div className="flex items-center gap-1">
                                {erp?.vencimento ? formatDate(erp.vencimento) : '—'}
                                {erp && xml && !vencMatch && <span title="Vencimento divergente do XML" className="inline-block h-1.5 w-1.5 rounded-full bg-warning" />}
                              </div>
                            </td>
                            <td className="px-3 py-2 text-right font-semibold tabular-nums text-tx">
                              <div className="flex items-center justify-end gap-1">
                                {erp ? money(erp.valorParcela) : '—'}
                                {erp && xml && !valMatch && <span title="Valor divergente do XML" className="inline-block h-1.5 w-1.5 rounded-full bg-warning" />}
                              </div>
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              {erp && erp.valorPendente && erp.valorPendente > 0 ? (
                                <span className="font-medium text-danger">{money(erp.valorPendente)}</span>
                              ) : (
                                <span className="text-tx-3">—</span>
                              )}
                            </td>
                            {/* Status */}
                            <td className="border-l border-line px-3 py-2"><StatusParcela erp={erp} /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="border-t border-line-strong bg-surface-2">
                      <tr>
                        <td className="px-3 py-2 text-[10px] font-semibold uppercase text-tx-3">Total</td>
                        <td className="border-l border-line px-3 py-2"></td>
                        <td className="px-3 py-2 text-right text-sm font-bold tabular-nums text-accent-text">{money(totalXml)}</td>
                        <td className="border-l border-line px-3 py-2"></td>
                        <td className="px-3 py-2 text-right text-sm font-bold tabular-nums text-ok">{money(totalErp)}</td>
                        <td className="px-3 py-2 text-right text-sm font-bold tabular-nums text-danger">{totalPendente > 0 ? money(totalPendente) : '—'}</td>
                        <td className="border-l border-line px-3 py-2"></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Legenda */}
                {xmlParcelas.length > 0 && erpParcelas.length > 0 && (
                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 px-1 text-[11px] text-tx-3">
                    <div className="flex items-center gap-1">
                      <span className="inline-block h-1.5 w-1.5 rounded-full bg-warning" /> Divergência entre XML e ERP
                    </div>
                    {xmlParcelas.length !== erpParcelas.length && (
                      <div className="flex items-center gap-1 text-warning">
                        <ExclamationCircleIcon className="h-3.5 w-3.5" /> Quantidade difere ({xmlParcelas.length} XML × {erpParcelas.length} ERP)
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Mensagem vazia */}
            {!isLoading && !error && parcelasData && mergedRows.length === 0 && (
              <div className="rounded-iris border border-line bg-surface-2 p-4 text-center text-sm text-tx-3">
                Esta NF-e não possui duplicatas — pagamento à vista ou sem parcelamento declarado.
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end border-t border-line bg-surface-2 px-4 py-3">
            <button onClick={onClose} className={btnSecundario}>Fechar</button>
          </div>
        </div>
      </div>
    </div>
  );
};

/** Situação da parcela no ERP — cor semântica (pago/aberto/não faturado). */
const StatusParcela: React.FC<{ erp?: { liquidado: boolean; ultimoPagamento?: string; tipoDescricao?: string } }> = ({ erp }) => {
  if (!erp) return (
    <span className="inline-flex items-center gap-1 text-[11px] text-tx-3"><ExclamationCircleIcon className="h-3.5 w-3.5" />Não faturada</span>
  );
  if (erp.liquidado) return (
    <span className="inline-flex items-center gap-1 text-[11px] font-medium text-ok" title={erp.tipoDescricao || ''}>
      <CheckCircleIcon className="h-3.5 w-3.5" />{erp.ultimoPagamento ? formatDate(erp.ultimoPagamento) : 'Paga'}
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 text-[11px] text-tx-2" title={erp.tipoDescricao || ''}>
      <StopCircleIcon className="h-3.5 w-3.5" />Em aberto
    </span>
  );
};

/** Grupo rotulado com lista de linhas (borda + divisores). */
const Grupo: React.FC<{ titulo: string; children: React.ReactNode }> = ({ titulo, children }) => (
  <div>
    <p className="mb-1.5 px-1 text-[10px] font-semibold uppercase tracking-wide text-tx-3">{titulo}</p>
    <div className="divide-y divide-line rounded-iris border border-line">{children}</div>
  </div>
);

/** Linha rótulo ··· valor (à direita). */
const Linha: React.FC<{ label: string; value: React.ReactNode; num?: boolean; valueClass?: string }> = ({ label, value, num, valueClass = 'text-tx' }) => (
  <div className="flex items-center justify-between gap-3 px-3 py-2">
    <span className="text-[13px] text-tx-3">{label}</span>
    <span className={`text-right text-sm font-medium ${valueClass} ${num ? 'tabular-nums' : ''}`}>{value}</span>
  </div>
);

export default ParcelasDialog;
